// ─────────────────────────────────────────────────────────────
// GÉNÉRATEUR DE CONTRAT DE LOCATION — IAM Business Group
// Reproduit fidèlement le modèle officiel (15 articles)
// Nécessite : npm install jspdf
// ─────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";
import IAM_LOGO_B64 from "../assets/logoData";
import { montantEnLettres } from "./quittance";

const NAVY = [26, 58, 92], GREY = [90, 122, 154], DARK = [30, 42, 58];
const W = 210, M = 18, BOTTOM = 278;

function fmtDateFR(iso) {
  if (!iso) return "___ / ___ / ______";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// ─────────────────────────────────────────────────────────────
// genererContrat(data) → retourne le doc jsPDF
//   data = {
//     chambre, dateEntree (ISO), duree, loyer, caution, preavisJours,
//     locataire1: { nom, naissance, telephone, etablissement },
//     locataire2: { nom, naissance, telephone, etablissement } | null,
//     bailleurRepresentant, faitA, faitLe (ISO),
//     signatureBailleur (dataURL|null),
//     signatureLocataire1 (dataURL|null),
//     signatureLocataire2 (dataURL|null),
//   }
// Le composant appelant fait doc.save(...) et/ou doc.output("blob")
// ─────────────────────────────────────────────────────────────
export function genererContrat(data) {
  const d = data || {};
  const loc1 = d.locataire1 || {};
  const loc2 = d.locataire2 || null;
  const loyer = parseInt(d.loyer) || 27500;
  const caution = parseInt(d.caution) || loyer;
  const total = loyer + caution;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = 0;

  const newPageIfNeeded = (needed = 12) => {
    if (y + needed > BOTTOM) { doc.addPage(); y = 22; }
  };
  const title = (t) => {
    newPageIfNeeded(16);
    doc.setFont("helvetica", "bold").setFontSize(11.5).setTextColor(...NAVY);
    doc.text(t, M, y); y += 7;
  };
  const para = (t, opts = {}) => {
    doc.setFont("helvetica", opts.bold ? "bold" : "normal")
       .setFontSize(opts.size || 10)
       .setTextColor(...(opts.color || DARK));
    const lines = doc.splitTextToSize(t, W - 2 * M - (opts.indent || 0));
    newPageIfNeeded(lines.length * 5.2 + 4);
    doc.text(lines, M + (opts.indent || 0), y, { lineHeightFactor: 1.45 });
    y += lines.length * 5.2 + (opts.gap ?? 3);
  };
  const bullet = (t, opts = {}) => {
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...DARK);
    const lines = doc.splitTextToSize(t, W - 2 * M - 8);
    newPageIfNeeded(lines.length * 5.2 + 2);
    doc.circle(M + 2.2, y - 1.4, 0.8, "F");
    doc.text(lines, M + 7, y, { lineHeightFactor: 1.4 });
    y += lines.length * 5.2 + (opts.gap ?? 1.5);
  };
  const champ = (label, valeur) => {
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...GREY);
    newPageIfNeeded(7);
    doc.text(label, M, y);
    doc.setFont("helvetica", "bold").setTextColor(...DARK);
    doc.text(String(valeur || "____________________________"), M + 46, y);
    y += 6.2;
  };

  // ═══ PAGE 1 — EN-TÊTE ═══
  try { doc.addImage(IAM_LOGO_B64.split(",")[1], "JPEG", W / 2 - 14, 14, 28, 28); } catch (e) {}
  y = 50;
  doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(...NAVY);
  doc.text("CONTRAT DE LOCATION D'UNE CHAMBRE NON MEUBLÉE", W / 2, y, { align: "center" });
  y += 7;
  doc.setFontSize(12);
  doc.text("RÉSIDENCE ÉTUDIANTE PRIVÉE", W / 2, y, { align: "center" });
  y += 4;
  doc.setDrawColor(...NAVY).setLineWidth(0.6).line(M + 30, y + 2, W - M - 30, y + 2);
  y += 12;

  // ═══ ENTRE ═══
  title("ENTRE :");
  para("LE BAILLEUR", { bold: true, gap: 1 });
  para("IAM Business Group", { bold: true, gap: 1 });
  champ("Adresse :", "Mammibougou, à côté de l'IPR/IFRA de Katibougou, Koulikoro — Mali");
  champ("Représenté par :", d.bailleurRepresentant || "");
  para("Ci-après appelé « le Bailleur »", { color: GREY, size: 9.5, gap: 6 });

  para("LE(S) LOCATAIRE(S)", { bold: true, gap: 2 });
  para("Locataire 1", { bold: true, gap: 1 });
  champ("Nom et Prénom :", loc1.nom);
  champ("Date de naissance :", loc1.naissance ? fmtDateFR(loc1.naissance) : "");
  champ("Téléphone :", loc1.telephone);
  champ("Établissement :", loc1.etablissement);
  y += 2;

  if (loc2 && loc2.nom) {
    para("Locataire 2", { bold: true, gap: 1 });
    champ("Nom et Prénom :", loc2.nom);
    champ("Date de naissance :", loc2.naissance ? fmtDateFR(loc2.naissance) : "");
    champ("Téléphone :", loc2.telephone);
    champ("Établissement :", loc2.etablissement);
    y += 2;
  }
  para("Ci-après appelé(s) « le Locataire »", { color: GREY, size: 9.5, gap: 8 });

  // ═══ ARTICLES ═══
  title("ARTICLE 1 — OBJET");
  para("Le Bailleur loue au Locataire une chambre non meublée avec douche intérieure, destinée uniquement à l'habitation d'étudiants.");
  para("Maximum autorisé : 2 étudiants", { bold: true, gap: 6 });

  title("ARTICLE 2 — LOCALISATION");
  para(`La chambre n° ${d.chambre || "____"} est située :`);
  para("Mammibougou, à côté de l'IPR/IFRA de Katibougou, Koulikoro — Mali", { bold: true, gap: 6 });

  title("ARTICLE 3 — DURÉE");
  champ("Date d'entrée :", fmtDateFR(d.dateEntree));
  champ("Durée :", d.duree || "");
  y += 4;

  title("ARTICLE 4 — LOYER");
  para(`Loyer mensuel : ${loyer.toLocaleString("fr-FR")} FCFA (${montantEnLettres(loyer)})`, { bold: true, gap: 4 });
  bullet("Le loyer est payable à l'avance");
  bullet("La caution doit être versée immédiatement pour réserver la chambre");
  bullet("Le premier loyer doit être payé avant l'occupation des lieux", { gap: 3 });
  para("Le loyer correspond à une période allant du 1er jour au dernier jour du mois (28, 29, 30 ou 31 selon le mois et l'année).");
  para("Si l'entrée dans la chambre a lieu en cours de mois, le montant du premier loyer sera calculé au prorata du nombre de jours restant jusqu'à la fin du mois.", { gap: 6 });

  title("ARTICLE 5 — CAUTION");
  para(`Une caution de : ${caution.toLocaleString("fr-FR")} FCFA (${montantEnLettres(caution)}) est demandée.`, { bold: true, gap: 3 });
  para(`Montant total à payer avant d'entrer : ${total.toLocaleString("fr-FR")} FCFA (loyer + caution)`, { bold: true, gap: 3 });
  para("La caution sera rendue au départ si :", { gap: 2 });
  bullet("La chambre est en bon état");
  bullet("Aucun loyer n'est dû");
  bullet("Les charges sont payées", { gap: 6 });

  title("ARTICLE 6 — CHARGES");
  para("Non incluses dans le loyer :", { gap: 2 });
  bullet("Électricité", { gap: 3 });
  para("Ces frais sont à la charge du Locataire.", { gap: 6 });

  title("ARTICLE 7 — ÉTAT DES LIEUX ET CONTRÔLE");
  bullet("Un contrôle sera fait à l'entrée et au départ");
  bullet("Une vérification sera faite environ une fois par mois");
  bullet("Les dégradations devront être réparées ou payées", { gap: 6 });

  title("ARTICLE 8 — PROPRETÉ ET HYGIÈNE");
  para("Le Locataire doit :", { gap: 2 });
  bullet("Garder la chambre propre");
  bullet("Entretenir la douche et les sanitaires");
  bullet("Éviter toute saleté excessive", { gap: 3 });
  para("Si l'hygiène est très mauvaise, le Bailleur peut demander au Locataire de quitter la chambre.", { gap: 6 });

  title("ARTICLE 9 — RÈGLES DE VIE");
  para("Pour assurer un bon cadre d'étude et la sécurité de tous :", { gap: 3 });
  para("Nuisances sonores — Interdits :", { bold: true, gap: 2 });
  bullet("Musique forte");
  bullet("Cris");
  bullet("Fêtes bruyantes");
  bullet("Tout bruit gênant les autres", { gap: 3 });
  para("Alcool", { bold: true, gap: 1.5 });
  para("La consommation d'alcool est interdite dans la chambre et dans la résidence.", { gap: 3 });
  para("Drogues", { bold: true, gap: 1.5 });
  para("Toute drogue ou substance illégale est strictement interdite.", { gap: 3 });
  para("Armes et objets dangereux", { bold: true, gap: 1.5 });
  para("Il est interdit de posséder ou d'introduire :", { gap: 2 });
  bullet("Armes à feu");
  bullet("Couteaux dangereux");
  bullet("Objets pouvant blesser");
  bullet("Tout objet dangereux pour les autres étudiants", { gap: 3 });
  para("Respect", { bold: true, gap: 1.5 });
  para("Le Locataire doit respecter :", { gap: 2 });
  bullet("Les autres étudiants");
  bullet("Le voisinage");
  bullet("Le Bailleur");
  bullet("Les installations", { gap: 6 });

  title("ARTICLE 10 — OCCUPATION");
  bullet("La chambre est réservée aux étudiants signataires");
  bullet("Maximum 2 occupants");
  bullet("Pas de sous-location");
  bullet("Pas d'hébergement permanent d'autres personnes", { gap: 6 });

  title("ARTICLE 11 — VISITES");
  bullet("Visites occasionnelles autorisées");
  bullet("Les invités ne doivent pas passer la nuit sans autorisation", { gap: 6 });

  title("ARTICLE 12 — RESPONSABILITÉ DES LOCATAIRES");
  para("Si deux étudiants occupent la chambre : ils sont responsables ensemble du paiement et des dégâts.", { gap: 6 });

  title("ARTICLE 13 — RÉSILIATION");
  para("Le contrat peut être arrêté :", { gap: 2 });
  para(`Par le Locataire — avec un préavis de ${d.preavisJours || "______"} jours`, { bold: true, gap: 3 });
  para("Par le Bailleur en cas de :", { bold: true, gap: 2 });
  bullet("Non-paiement");
  bullet("Dégradations");
  bullet("Mauvaise hygiène");
  bullet("Bruits excessifs");
  bullet("Alcool, drogues ou armes");
  bullet("Non-respect des règles");
  bullet("Plus de 2 occupants", { gap: 6 });

  title("ARTICLE 14 — RESPONSABILITÉ POUR LES BIENS PERSONNELS");
  para("Le Bailleur n'est pas responsable en cas de :", { gap: 2 });
  bullet("Vol");
  bullet("Incendie");
  bullet("Dégâts");
  bullet("Perte d'objets personnels", { gap: 6 });

  title("ARTICLE 15 — DROIT APPLICABLE");
  para("Ce contrat est soumis aux lois du Mali.", { gap: 10 });

  // ═══ SIGNATURES ═══
  newPageIfNeeded(85);
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...DARK);
  doc.text(`FAIT À : ${d.faitA || "____________________"}`, M, y); y += 7;
  doc.text(`LE : ${fmtDateFR(d.faitLe || new Date().toISOString())}`, M, y); y += 12;

  const sigBlock = (label, nom, sigDataUrl, x) => {
    doc.setFont("helvetica", "bold").setFontSize(10.5).setTextColor(...NAVY);
    doc.text(label, x, y);
    doc.setFont("helvetica", "normal").setFontSize(9.5).setTextColor(...GREY);
    doc.text(`Nom : ${nom || "______________________"}`, x, y + 7);
    if (sigDataUrl) {
      try { doc.addImage(sigDataUrl, "PNG", x, y + 10, 55, 22); } catch (e) {}
    }
    doc.setDrawColor(...GREY).setLineWidth(0.3).line(x, y + 34, x + 62, y + 34);
    doc.setFontSize(8).text("Signature", x, y + 39);
  };

  sigBlock("LE BAILLEUR", d.bailleurRepresentant ? `IAM Business Group — ${d.bailleurRepresentant}` : "IAM Business Group", d.signatureBailleur, M);
  sigBlock("LE LOCATAIRE 1", loc1.nom, d.signatureLocataire1, W / 2 + 6);
  y += 48;

  if (loc2 && loc2.nom) {
    newPageIfNeeded(50);
    sigBlock("LE LOCATAIRE 2", loc2.nom, d.signatureLocataire2, M);
    y += 48;
  }

  // Pied de page sur chaque page
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(150, 165, 180);
    doc.text(`Contrat de location IAM Business Group · Chambre ${d.chambre || "—"} · Page ${i}/${pages}`, W / 2, 290, { align: "center" });
  }

  return doc;
}
